import { useAppSelector, useAppTranslation } from 'app/hooks';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Pagination, Select, Table } from 'antd';
import { formatDate } from 'utils/dateUtils';
import { useMessageSuccess } from 'hooks/useMessageSuccess';
import { platformStates } from 'redux/platformSlice';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { OrderTransactionResponseModel } from 'models/transaction';
import transactionApi from 'api/transactionApi';
import EmptyTableIcon from 'assets/icons/EmptyTableIcon';
import { TRANSACTION_STATUS } from 'consts/app';
import LoadingSpinner from 'components/Commons/LoadingSpinner';
import useUserDetail from 'hooks/useUserDetail';
import { formatAmountV2 } from 'utils/formatAmount';

interface SwapTransactionModalProps { }

interface RowDataProps {
  title: string;
  objectKey: string;
}

const RowData: RowDataProps[] = [
  {
    title: 'transactionHistory.header.id',
    objectKey: 'stt',
  },
  {
    title: 'transactionHistory.header.from',
    objectKey: 'from',
  },
  {
    title: 'transactionHistory.header.to',
    objectKey: 'to',
  },
  {
    title: 'transactionHistory.header.amount',
    objectKey: 'amount',
  },
  {
    title: 'transactionHistory.header.receivedAmount',
    objectKey: 'amountReceived',
  },
  {
    title: 'transactionHistory.header.usdAmount',
    objectKey: 'usdAmount',
  },
  {
    title: 'transactionHistory.header.rate',
    objectKey: 'rate',
  },
  {
    title: 'transactionHistory.header.status.title',
    objectKey: 'status',
  },
  {
    title: 'transactionHistory.header.confirmedTime',
    objectKey: 'createdDate',
  },
];

const SwapTransactionModal: React.FunctionComponent<SwapTransactionModalProps> = () => {
  const { isDesktop, isLaptop } = useAppSelector(platformStates);
  const t = useAppTranslation();
  const { showToast, contextHolder } = useMessageSuccess();
  const handleResponseError = useHandleResponseError();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<OrderTransactionResponseModel[]>([]);
  const [totalTransactions, setTotalTransactions] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [status, setStatus] = useState<string>('');
  const { currentUser } = useUserDetail();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const { ok, body, error, pagination } = await transactionApi.getOrderSwapTransaction({
      sort: 'id,desc',
      size: 10,
      page: currentPage,
      'status.in': status || null,
    });

    setIsLoading(false);

    if (ok && body) {
      setTransactions(
        body.map((t, index) => ({
          ...t,
          key: t.id,
          stt: currentPage * 10 + index + 1,
          from: t.reversed ? t.coinPair.quote.symbol : t.coinPair.base.symbol,
          to: !t.reversed ? t.coinPair.quote.symbol : t.coinPair.base.symbol,
        })),
      );
      setTotalTransactions(pagination?.total || 0);
    }

    handleResponseError(error);
  }, [currentPage, status, currentUser?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData, currentPage]);

  const handleCopyToClipboard = useCallback(
    (value: string) => {
      navigator.clipboard.writeText(value);
      showToast('Copy to clipboard!');
    },
    [showToast],
  );

  const tableColumns = useMemo((): ColumnsType<OrderTransactionResponseModel> => {
    return [
      {
        title: t('transactionHistory.header.id'),
        dataIndex: 'stt',
        align: 'center',
      },
      {
        title: t('transactionHistory.header.from'),
        dataIndex: 'from',
        render: (text, record) => (
          <div className="flex-row align-center justify-between ">
            <span className="">{record.coinPair.quote.symbol}</span>
          </div>
        ),
      },
      {
        title: t('transactionHistory.header.to'),
        dataIndex: 'to',
        render: (text, record) => (
          <div className="flex-row align-center justify-between ">
            <span className="">{record.coinPair.base.symbol}</span>
          </div>
        ),
      },
      {
        title: t('transactionHistory.header.amount'),
        dataIndex: 'amount',
        width: 100,
        render: (text, record) => (
          <span className="nowrap">$ {formatAmountV2(Number(record.amount), 8)}</span>
        ),
      },
      {
        title: t('transactionHistory.header.receivedAmount'),
        dataIndex: 'amountReceived',
        width: 100,
        render: (text, record) => (
          <span className="nowrap">$ {formatAmountV2(Number(record.amountReceived), 8)}</span>
        ),
      },
      {
        title: t('transactionHistory.header.usdAmount'),
        dataIndex: 'usdAmount',
        width: 100,
        render: (text, record) => (
          <span className="nowrap">
            $ {formatAmountV2(Number(record.amountReceived) * record.quoteUsdRate, 8)}
          </span>
        ),
      },
      {
        title: t('transactionHistory.header.rate'),
        dataIndex: 'rate',
        width: 100,
      },
      {
        title: t('transactionHistory.header.status.title'),
        dataIndex: 'status',
        render: (value) => (
          <span className={`${TRANSACTION_STATUS[value].className}`}>
            {t(TRANSACTION_STATUS[value].content)}
          </span>
        ),
      },
      {
        title: t('transactionHistory.header.confirmedTime'),
        dataIndex: 'createdDate',
        render: (value) => <span>{value ? formatDate(value) : '--'}</span>,
      },
    ];
  }, [handleCopyToClipboard]);

  const handleOnChangeStatus = (value: string) => {
    setStatus(value);
    setCurrentPage(0);
  };

  return (
    <Fragment>
      <div className="search-group gap-4"></div>
      {isDesktop && !isLaptop ? (
        <Table
          className="common-table"
          columns={tableColumns}
          dataSource={transactions}
          pagination={{
            position: ['bottomRight'],
            total: totalTransactions,
            pageSize: 10,
            current: currentPage + 1,
            onChange: (page) => setCurrentPage(page - 1),
          }}
          bordered
          tableLayout="auto"
          loading={false}
        />
      ) : (
        <Fragment>
          {transactions.length !== 0 ? (
            <Fragment>
              <div className="transaction-list cashier-list">
                {transactions.map((transaction) => (
                  <div className="transaction" key={`transaction-${transaction.id}`}>
                    {RowData.map((row, index) => {
                      let value =
                        transaction[row.objectKey as keyof typeof transaction]?.toString() || '';

                      if (row.objectKey === 'from') value = transaction.coinPair.quote.symbol;

                      if (row.objectKey === 'to') value = transaction.coinPair.base.symbol;

                      if (row.objectKey === 'createdDate')
                        value = value ? formatDate(value) : '--';

                      if (row.objectKey === 'usdAmount')
                        value = `$ ${formatAmountV2(
                          Number(transaction.amountReceived) * transaction.quoteUsdRate,
                          8,
                        )}`;

                      if (row.objectKey === 'amount')
                        value = `$ ${formatAmountV2(Number(transaction.amount), 8)}`;

                      if (row.objectKey === 'amountReceived')
                        value = `$ ${formatAmountV2(Number(transaction.amountReceived), 8)}`;

                      if (row.objectKey === 'type') {
                        value =
                          value === 'DEPOSIT'
                            ? t('transactionHistory.header.type.deposit')
                            : t('transactionHistory.header.type.withdraw');
                      }
                      return (
                        <div className="row" key={`transaction-${transaction.id}-row-${index}`}>
                          <span>{t(row.title)}</span>
                          <span
                            className={`${row.objectKey === 'status' ? TRANSACTION_STATUS[value].className : ''
                              } ${row.objectKey === 'id' ? 'font-bold' : ''}`}
                          >
                            {row.objectKey === 'status'
                              ? t(TRANSACTION_STATUS[value].content)
                              : value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="flex-row align-center justify-center mt-2">
                <Pagination
                  current={currentPage + 1}
                  total={totalTransactions}
                  onChange={(page) => setCurrentPage(page - 1)}
                  pageSize={10}
                  className="common-pagination"
                />
              </div>
            </Fragment>
          ) : (
            <div className="no-data">
              <EmptyTableIcon textColor="white" />
            </div>
          )}
        </Fragment>
      )}
      {isLoading && (
        <div className="loading">
          <LoadingSpinner className="common__loading" />
        </div>
      )}
      {contextHolder}
    </Fragment>
  );
};

export default SwapTransactionModal;
